import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/honest-answers/red-flags");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/honest-answers/red-flags")({
  head: () => ({
    meta: [
      {
        title:
          "10 Red Flags When Hiring a Colorado SEO or Web Design Company",
      },
      {
        name: "description",
        content:
          "20 years of cleanup work boiled down to 10 warning signs. If your agency does any of these, get out before they take another invoice.",
      },
      {
        property: "og:title",
        content: "10 red flags when hiring a Colorado web/SEO agency",
      },
      {
        property: "og:description",
        content:
          "Real warning signs from 20 years of cleaning up other agencies' messes in Colorado.",
      },
      { property: "og:url", content: `${SITE_URL}/honest-answers/red-flags` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/honest-answers/red-flags" }],
  }),
  component: RedFlagsArticle,
});

const flags = [
  {
    n: "01",
    flag: "They guarantee a #1 Google ranking.",
    why: "Nobody can guarantee #1. Not us, not anyone. Google explicitly says any agency promising rankings is lying. This one is a hard pass — every time, no exceptions.",
  },
  {
    n: "02",
    flag: "Pricing is a secret until you book a call.",
    why: "If they won&rsquo;t post starting prices, it&rsquo;s because pricing is whatever they think they can extract from you. Real agencies have ranges they&rsquo;ll publish.",
  },
  {
    n: "03",
    flag: "12-month contracts before any work is shown.",
    why: "Long contracts protect bad agencies, not good ones. We work month-to-month — if we&rsquo;re not delivering, you walk. Anyone needing a year of your money up front is hedging against their own quality.",
  },
  {
    n: "04",
    flag: "You don&rsquo;t own the website, the domain, or the hosting.",
    why: "&ldquo;Free website with our hosting&rdquo; is a hostage situation. The day you try to leave, you lose everything. Always demand ownership in writing.",
  },
  {
    n: "05",
    flag: "They send you reports full of vanity metrics.",
    why: "&ldquo;Impressions up 400%!&rdquo; means nothing if calls are flat. Real reports show calls, form fills, booked jobs, and revenue — not just traffic graphs.",
  },
  {
    n: "06",
    flag: "Cold-called you from out of state.",
    why: "If a Texas agency cold-called your Colorado roofing business promising the moon, they don&rsquo;t know your market. They&rsquo;re running a boiler-room. Hang up.",
  },
  {
    n: "07",
    flag: "AI-generated content with no human edits.",
    why: "Not anti-AI — we use it. But pure AI content with nobody fact-checking, nobody adding real Colorado specifics, and nobody writing in your voice is going to tank your rankings and embarrass you.",
  },
  {
    n: "08",
    flag: "They won&rsquo;t tell you who&rsquo;s actually doing the work.",
    why: "If &ldquo;our team&rdquo; is offshore subcontractors, you deserve to know. Not because offshore is bad — because if your account manager is just a salesperson with no technical depth, your strategy is going to suffer.",
  },
  {
    n: "09",
    flag: "Hidden ad spend and zero transparency on Google Ads.",
    why: "Some agencies bill you $3K/month for &ldquo;ads management&rdquo; without ever showing the actual ad-spend account. You should have direct login access to your Google Ads account at all times. Always.",
  },
  {
    n: "10",
    flag: "Pressure to sign in the first call.",
    why: "If you feel rushed, that&rsquo;s the strategy. Good agencies want a good fit and will happily wait a week for you to think. Pressure = they need to close you before you talk to anyone else.",
  },
];

function RedFlagsArticle() {
  return (
    <ArticleLayout
      eyebrow="Buyer's Guide"
      read="8 min read"
      title="10 red flags when hiring a Colorado SEO or web design company"
      intro="I&rsquo;ve spent 20 years cleaning up after other agencies in Colorado. The same patterns burn business owners over and over. Here are the 10 warning signs that should make you walk away — even if the pitch sounds great."
      path="/honest-answers/red-flags"
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Honest Answers", to: "/honest-answers" },
        { label: "Agency Red Flags" },
      ]}
      faqs={[
        { q: "What's the #1 red flag when hiring a marketing agency?", a: "Guaranteed #1 rankings. Nobody can guarantee Google rankings — and any agency that claims to is either lying or about to use spammy tactics that get you penalized." },
        { q: "Is it a red flag if an agency won't share their process?", a: "Yes. Honest agencies will walk you through exactly what they do, who does it, and how they measure success. Vague answers usually mean they don't actually do the work themselves." },
        { q: "What about cold-call agencies that found problems with my site?", a: "Almost always a scam or AI-generated bot. Real agencies don't cold-call with scare tactics. If someone tells you your site 'sucks' to book a meeting, hang up." },
      ]}
    >
      <p>
        If your current or prospective agency is doing any of the things below, take it seriously.
        Most of these aren&rsquo;t single mistakes — they&rsquo;re patterns of how that agency
        operates. The pattern almost always continues.
      </p>

      <div className="not-prose my-10 space-y-4">
        {flags.map((f) => (
          <div
            key={f.n}
            className="overflow-hidden rounded-2xl border border-border bg-card shadow-card"
          >
            <div className="grid gap-0 md:grid-cols-[auto_1fr]">
              <div className="flex items-center justify-center bg-gradient-dark px-6 py-5 md:px-8 md:py-6">
                <span className="text-4xl font-black text-gradient-gold md:text-5xl">{f.n}</span>
              </div>
              <div className="p-6">
                <h3
                  className="text-lg font-bold text-foreground"
                  dangerouslySetInnerHTML={{ __html: f.flag }}
                />
                <p
                  className="mt-2 text-sm leading-relaxed text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: f.why }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <h2>What to do instead</h2>
      <p>
        Before you sign anything with any agency — including us — ask for these in writing:
      </p>
      <ul>
        <li>Starting prices and what&rsquo;s included at each tier</li>
        <li>Month-to-month terms (or a clear, fair exit clause)</li>
        <li>Full ownership of your site, domain, hosting, and ad accounts</li>
        <li>The name of the person actually doing your work</li>
        <li>Sample reports showing leads, calls, and revenue — not just traffic</li>
        <li>References from at least 3 current Colorado clients</li>
      </ul>
      <p>
        If they hesitate on any of those, you have your answer. Want to see how we stack up?{" "}
        <Link to="/about" className="font-bold text-brand-gold-deep underline">
          Read about us
        </Link>{" "}
        or{" "}
        <Link to="/are-we-a-fit" className="font-bold text-brand-gold-deep underline">
          take the 2-minute fit quiz
        </Link>
        .
      </p>
    </ArticleLayout>
  );
}
